import {SceneData, SceneEntity} from './types.ts';

export type SceneTreeRow = {
    elementId: string;
    entity: SceneEntity;
    depth: number;
};

// The group an entity is in, or null when its `parent` names no group in the
// data (the runtime ignores those, so they show at the top level).
export function parentGroupId(
    sceneData: SceneData,
    elementId: string
): string | null {
    const parent = sceneData[elementId]?.parent;
    return parent && parent !== elementId && sceneData[parent]?.type === 'group'
        ? parent
        : null;
}

// Whether elementId is ancestorId or somewhere inside it.
export function isInside(
    sceneData: SceneData,
    elementId: string,
    ancestorId: string
): boolean {
    const seen = new Set<string>();
    let current: string | null = elementId;
    while (current && !seen.has(current)) {
        if (current === ancestorId) {
            return true;
        }
        seen.add(current);
        current = parentGroupId(sceneData, current);
    }
    return false;
}

// The objects in list order: each group followed by its children, indented.
// The sceneSettings row isn't an object, so it's left out.
export function buildSceneTree(sceneData: SceneData): SceneTreeRow[] {
    const objects = Object.entries(sceneData).filter(
        ([, entity]) => entity.type !== 'sceneSettings'
    );

    const childrenOf = new Map<string | null, [string, SceneEntity][]>();
    objects.forEach(([elementId, entity]) => {
        const parent = parentGroupId(sceneData, elementId);
        childrenOf.set(parent, [
            ...(childrenOf.get(parent) ?? []),
            [elementId, entity]
        ]);
    });

    const rows: SceneTreeRow[] = [];
    const visited = new Set<string>();
    const visit = (elementId: string, entity: SceneEntity, depth: number) => {
        if (visited.has(elementId)) {
            return;
        }
        visited.add(elementId);
        rows.push({elementId, entity, depth});
        (childrenOf.get(elementId) ?? []).forEach(([childId, child]) =>
            visit(childId, child, depth + 1)
        );
    };

    (childrenOf.get(null) ?? []).forEach(([elementId, entity]) =>
        visit(elementId, entity, 0)
    );
    // Groups that are inside each other never reach the top level above:
    // list what's left there instead of hiding it.
    objects.forEach(([elementId, entity]) => visit(elementId, entity, 0));

    return rows;
}
